/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Compiled supplies the entrance keyframes and private completion timing variables.
import { cssMap, jsx, keyframes } from '@compiled/react';

import { useIsReducedMotion } from '@atlaskit/motion/use-is-reduced-motion';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { RovoThinkingMorphContext } from './RovoThinkingMorphContext';
import { RovoThinkingPill, type RovoThinkingPillProps } from './RovoThinkingPill';

const CONTENT_FADE_OUT_MS = 150;
// Match motion.duration.xlong for the shell resize and content reveal.
const RESIZE_DURATION_MS = 400;
const CONTENT_FADE_IN_MS = 0;
const SURFACE_BORDER_WIDTH = 1;

const slideUp = keyframes({
	from: { opacity: 0, transform: 'translateY(100%)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const styles = cssMap({
	container: {
		animationName: slideUp,
		animationDuration: token('motion.duration.medium'),
		animationTimingFunction: token('motion.easing.out.bold'),
		contain: 'inline-size',
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		'@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
	},
	surface: {
		'--rovo-thinking-completion-fade-duration': token('motion.duration.xlong'),
		width: '100%',
		position: 'relative',
		flexShrink: 0,
		marginInline: 'auto',
		overflow: 'visible',
		borderRadius: `var(--rovo-thinking-border-radius, ${token('radius.xlarge')})`,
		// This neutral backdrop bridges the original views while their contents are hidden.
		backgroundColor: token('elevation.surface.overlay'),
		// Bridge the original views' shadows while both views are fading out or hidden.
		'&::before': {
			content: '""',
			position: 'absolute',
			inset: 0,
			borderRadius: 'inherit',
			boxShadow: token('elevation.shadow.overlay'),
			opacity: 0,
			pointerEvents: 'none',
			transitionProperty: 'opacity',
			transitionDuration: '0ms',
			'@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms' },
		},
		transitionProperty: 'width, height, border-radius',
		transitionDuration: token('motion.duration.xlong'),
		transitionTimingFunction: `cubic-bezier(0.2, 1.5, 0.4, 1), ${token('motion.easing.inout.bold')}, ${token('motion.easing.inout.bold')}`,
		'@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms' },
	},
	transitionShadow: { '&::before': { opacity: 1 } },
	rovoSurface: { backgroundColor: token('elevation.rovo.surface.overlay') },
	compactSurface: {
		transitionTimingFunction: `cubic-bezier(0.2, 1.25, 0.4, 1), ${token('motion.easing.inout.bold')}, ${token('motion.easing.inout.bold')}`,
	},
	initialSurface: { transitionDuration: '0ms' },
	completedSurface: {
		transitionDuration: token('motion.duration.xlong'),
		'@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms' },
	},
	full: {
		'--rovo-thinking-loading-width': `calc(var(--rovo-thinking-bar-width) - 2 * ${token('space.100')})`,
		position: 'absolute',
		top: '50%',
		left: 0,
		transform: 'translateY(-50%)',
		width: '100%',
		transitionProperty: 'opacity, visibility',
		transitionDuration: '0ms',
		'@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms' },
	},
	pill: {
		position: 'absolute',
		top: '50%',
		left: 0,
		transform: 'translateY(-50%)',
		width: 'max-content',
		transitionProperty: 'opacity, visibility',
		transitionDuration: '0ms',
		'@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms' },
	},
	fadingOut: {
		transitionDuration: token('motion.duration.short'),
		'@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms' },
	},
	hidden: { visibility: 'hidden', opacity: 0, pointerEvents: 'none' },
});

type Dimensions = {
	containerWidth: number;
	fullWidth: number;
	fullHeight: number;
	pillWidth: number;
	pillHeight: number;
};

/** Keeps both views mounted and measures them independently of the animated surface. */
export function RovoThinkingMorph({
	children,
	pillProps,
	onExpand,
	isMinimized,
	isThinking,
	onDisplayedMinimizedChange,
}: {
	children: ReactNode;
	pillProps?: Omit<RovoThinkingPillProps, 'onClick'>;
	onExpand: () => void;
	isMinimized: boolean;
	isThinking: boolean;
	onDisplayedMinimizedChange?: (isMinimized: boolean) => void;
}): JSX.Element {
	const containerRef = useRef<HTMLDivElement>(null);
	const fullRef = useRef<HTMLDivElement>(null);
	const pillRef = useRef<HTMLDivElement>(null);
	const [dimensions, setDimensions] = useState<Dimensions | null>(null);
	const [intrinsicContent, setIntrinsicContent] = useState<HTMLDivElement | null>(null);
	const prefersReducedMotion = useIsReducedMotion();
	const [isCompact, setIsCompact] = useState(isMinimized);
	const [hasMorphed, setHasMorphed] = useState(isMinimized);
	const [showFullContent, setShowFullContent] = useState(!isMinimized);
	const [showPillContent, setShowPillContent] = useState(isMinimized);
	const [expansionPending, setExpansionPending] = useState(false);
	const previousIsMinimized = useRef(isMinimized);
	const displayedIsMinimized = useRef(isMinimized);
	useEffect(() => {
		if (displayedIsMinimized.current !== isMinimized) {
			displayedIsMinimized.current = isMinimized;
			onDisplayedMinimizedChange?.(isMinimized);
		}
	}, [isMinimized, onDisplayedMinimizedChange]);

	const isExpanding = !prefersReducedMotion && (expansionPending || (!isMinimized && isCompact));
	const surfaceIsThinking = isThinking || isExpanding;

	useLayoutEffect(() => {
		const wasMinimized = previousIsMinimized.current;
		previousIsMinimized.current = isMinimized;
		if (isMinimized) {
			setHasMorphed(true);
		}
		if (prefersReducedMotion) {
			setIsCompact(isMinimized);
			setShowFullContent(!isMinimized);
			setShowPillContent(isMinimized);
			setExpansionPending(false);
			return;
		}
		if (isMinimized) {
			setExpansionPending(false);
			if (wasMinimized) {
				setIsCompact(true);
				setShowPillContent(true);
				return;
			}
			// Fade the complete original bar before shrinking the neutral container.
			setShowFullContent(false);
			setShowPillContent(false);
			const resizeTimer = setTimeout(() => setIsCompact(true), CONTENT_FADE_OUT_MS);
			const revealTimer = setTimeout(
				() => setShowPillContent(true),
				CONTENT_FADE_OUT_MS + RESIZE_DURATION_MS,
			);
			return () => {
				clearTimeout(resizeTimer);
				clearTimeout(revealTimer);
			};
		}
		setIsCompact(false);
		setShowPillContent(false);
		if (!wasMinimized) {
			return;
		}
		// Automatic completion follows exactly the same expansion as a pill click.
		setExpansionPending(true);
		const revealTimer = setTimeout(() => setShowFullContent(true), RESIZE_DURATION_MS);
		const finishTimer = setTimeout(
			() => setExpansionPending(false),
			RESIZE_DURATION_MS + CONTENT_FADE_IN_MS,
		);
		return () => {
			clearTimeout(revealTimer);
			clearTimeout(finishTimer);
		};
	}, [isMinimized, prefersReducedMotion]);

	const restoreFocus = useRef(false);
	useLayoutEffect(() => {
		const full = fullRef.current;
		const pill = pillRef.current;
		const container = containerRef.current;
		if (!full || !pill || !container) {
			return;
		}
		const active = container.ownerDocument.activeElement;
		if (
			restoreFocus.current &&
			active !== container &&
			!full.contains(active) &&
			!pill.contains(active)
		) {
			restoreFocus.current = false;
		}
		if (
			(!showFullContent && full.contains(active)) ||
			(!showPillContent && pill.contains(active))
		) {
			restoreFocus.current = true;
		}
		full.inert = !showFullContent;
		pill.inert = !showPillContent;
		if (restoreFocus.current) {
			const incoming = showFullContent ? full : showPillContent ? pill : null;
			const action = incoming?.querySelector<HTMLElement>('button, [href], [tabindex="0"]');
			(action ?? container).focus();
			if (action) {
				restoreFocus.current = false;
			}
		}
	}, [showFullContent, showPillContent]);

	useLayoutEffect(() => {
		const container = containerRef.current;
		const full = fullRef.current;
		const compact = pillRef.current;
		if (!container || !full || !compact) {
			return;
		}

		const measure = () => {
			const containerWidth = container.getBoundingClientRect().width;
			// A hidden host (for example while chat is open) has no useful measurements.
			if (containerWidth === 0) {
				return;
			}
			const fullRect = full.getBoundingClientRect();
			// Loading content follows the host width; only results may grow to fit actions.
			const intrinsicRect = !surfaceIsThinking
				? intrinsicContent?.getBoundingClientRect()
				: undefined;
			// Measure padding from the start edge, which stays fixed while the shell grows.
			// The opposite edge crosses the ready content during expansion and is not padding.
			const isRtl = intrinsicContent && getComputedStyle(intrinsicContent).direction === 'rtl';
			const inlinePadding = intrinsicRect
				? Math.abs(
						isRtl ? fullRect.right - intrinsicRect.right : intrinsicRect.left - fullRect.left,
					)
				: 0;
			// Flex actions can overflow the measured box; include their full scroll width.
			const contentWidth = intrinsicRect
				? Math.max(intrinsicRect.width, intrinsicContent?.scrollWidth ?? 0)
				: 0;
			const intrinsicWidth = intrinsicRect ? contentWidth + inlinePadding * 2 : 0;
			const fullWidth = Math.max(containerWidth, intrinsicWidth);
			const fullHeight = fullRect.height;
			const pillRect = compact.getBoundingClientRect();
			const pillWidth = pillRect.width;
			const pillHeight = pillRect.height;
			setDimensions((previous) =>
				previous?.containerWidth === containerWidth &&
				previous.fullWidth === fullWidth &&
				previous.fullHeight === fullHeight &&
				previous.pillWidth === pillWidth &&
				previous.pillHeight === pillHeight
					? previous
					: { containerWidth, fullWidth, fullHeight, pillWidth, pillHeight },
			);
		};

		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(container);
		observer.observe(full);
		observer.observe(compact);
		if (intrinsicContent) {
			observer.observe(intrinsicContent);
		}
		return () => observer.disconnect();
	}, [intrinsicContent, surfaceIsThinking]);

	const fullStyle = {
		'--rovo-thinking-bar-width': dimensions
			? `${dimensions.containerWidth - SURFACE_BORDER_WIDTH * 2}px`
			: undefined,
		width: dimensions ? (surfaceIsThinking ? dimensions.containerWidth : '100%') : undefined,
	};

	const surfaceStyle = {
		width: isCompact ? dimensions?.pillWidth : dimensions?.fullWidth,
		height: isCompact ? dimensions?.pillHeight : dimensions?.fullHeight,
		'--rovo-thinking-border-radius':
			isCompact && dimensions ? `${dimensions.pillHeight / 2}px` : undefined,
	};

	return (
		<div
			ref={containerRef}
			tabIndex={-1}
			css={styles.container}
			style={{
				height: dimensions ? Math.max(dimensions.fullHeight, dimensions.pillHeight) : undefined,
			}}
		>
			<div
				data-testid="rovo-thinking-morph"
				css={[
					styles.surface,
					!showFullContent && !showPillContent && styles.transitionShadow,
					isCompact && styles.compactSurface,
					!surfaceIsThinking && styles.completedSurface,
					!hasMorphed && surfaceIsThinking && styles.initialSurface,
					fg('platform_editor_ai_rovo_bar_ui_update') && styles.rovoSurface,
				]}
				style={surfaceStyle}
			>
				<RovoThinkingMorphContext.Provider
					value={{
						isMorph: true,
						isExpanding,
						onIntrinsicContentChange: setIntrinsicContent,
					}}
				>
					<div
						ref={fullRef}
						data-testid="rovo-thinking-morph-full"
						aria-hidden={!showFullContent}
						css={[
							styles.full,
							!showFullContent && styles.hidden,
							!showFullContent && styles.fadingOut,
						]}
						style={fullStyle}
					>
						{children}
					</div>
					<div
						ref={pillRef}
						aria-hidden={!showPillContent}
						css={[styles.pill, !showPillContent && styles.hidden]}
						style={{ maxWidth: dimensions?.fullWidth }}
					>
						<RovoThinkingPill {...pillProps} onClick={onExpand} />
					</div>
				</RovoThinkingMorphContext.Provider>
			</div>
		</div>
	);
}
